import { useEffect, useRef, useState } from 'react';
import { Send, Sparkles, Bot, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChatMessage } from '@/components/assistant/ChatMessage';
import { Assistant as AssistantApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const SUGGESTIONS = [
  'Which card should I use for insurance?',
  'Best card for international travel?',
  'Am I wasting rewards this month?',
  'How close am I to my Atlas tier?',
  'Compare HDFC Infinia vs ICICI Emeralde for utilities',
  'Best card for ₹4,200 on Swiggy?',
];

export default function Assistant() {
  const { user, demoLogin } = useAuth();
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi — I’m your rewards concierge. Ask me anything about your cards, milestones, or leakage. I’ll never guess; my answers are grounded in your portfolio.' },
  ]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { if (!user) demoLogin().catch(() => {}); }, [user, demoLogin]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, thinking]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q) return;
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setInput('');
    setThinking(true);
    try {
      const res = await AssistantApi.chat(sessionId, q);
      if (res.session_id) setSessionId(res.session_id);
      setMessages((m) => [...m, { role: 'assistant', content: res.message }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: 'I had trouble fetching an answer just now. Try again in a moment.' }]);
    } finally {
      setThinking(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-accent font-medium">Concierge</div>
        <h1 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2"><Bot className="h-6 w-6 sm:h-7 sm:w-7 text-accent" /> AI Assistant</h1>
        <p className="mt-1 text-sm text-muted-foreground">Powered by GPT-5 — grounded in your portfolio. Never computes rewards (the deterministic engine does that).</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        <Card className="p-4 h-fit lg:sticky lg:top-20">
          <div className="flex items-center gap-2 text-sm font-medium"><Sparkles className="h-4 w-4 text-accent" /> Suggested</div>
          <div className="mt-3 space-y-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} className="w-full text-left text-sm px-3 py-2 rounded-lg border border-border bg-[hsl(var(--surface-2))] hover:bg-[hsl(var(--surface-3))] transition-colors">{s}</button>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-3 p-0 overflow-hidden">
          <ScrollArea className="h-[520px] p-6">
            <div className="space-y-4">
              {messages.map((m, i) => <ChatMessage key={i} role={m.role} content={m.content} />)}
              {thinking && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground pl-11">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                </div>
              )}
              <div ref={endRef} />
            </div>
          </ScrollArea>
          <div className="p-4 border-t border-border bg-[hsl(var(--surface-2))]">
            <div className="flex items-end gap-2">
              <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Ask about a card, spend, milestone..." rows={1} className="min-h-[44px] max-h-[120px] resize-none" data-testid="assistant-chat-input" />
              <Button onClick={() => send()} disabled={thinking} className="h-[44px] px-4 gap-1.5" data-testid="assistant-send-button"><Send className="h-4 w-4" /> Send</Button>
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">Press Enter to send. Responses are scoped to your portfolio.</div>
          </div>
        </Card>
      </div>
    </div>
  );
}
